import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { posApi, reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import ItemServiceSelect from '@/components/common/ItemServiceSelect';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

export default function ItemsSaleSummarySection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [category, setCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [page, setPage] = useState(0);

  const { data: categoriesRes } = useQuery({
    queryKey: ['productCategoriesForItemsSaleSummary'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Product' });
      return res.data.message;
    },
  });

  const categories: { name: string; category_name: string }[] = categoriesRes || [];



  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['itemsSaleSummary', from_date, to_date, category, selectedItem],
    queryFn: () =>
      reportsApi.getItemsSaleSummary({
        from_date,
        to_date,
        category: category || undefined,
        item: selectedItem || undefined,
      }),
  });

  useEffect(() => {
    setPage(0);
  }, [rows, from_date, to_date, category, selectedItem]);

  const handleExport = () => {
    if (!rows.length) return;
    const exportRows = rows.map((row: any) => ({
      ...row,
      item_name: row.variant_name ? `${row.item_name} — ${row.variant_name}` : row.item_name,
    }));
    downloadCsv(
      `items-sale-summary-${from_date}-${to_date}.csv`,
      [
        { key: 'item_code', label: 'Item Code' },
        { key: 'item_name', label: 'Item Name' },
        { key: 'qty_sold', label: 'Qty Sold' },
        { key: 'sales_amount', label: 'Sales Amount' },
        // { key: 'discount', label: 'Discount' },
        // { key: 'net_amount', label: 'Net Amount' },
        { key: 'avg_selling_price', label: 'Avg Selling Price' },
      ],
      exportRows as Record<string, unknown>[]
    );
  };

  const pageRows = rows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700">Category</label>
            <select
              className="input-field"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setSelectedItem('');
              }}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-gray-700">Item</label>
            <ItemServiceSelect
              type="Item"
              value={selectedItem}
              onChange={setSelectedItem}
              placeholder="Search items..."
            />
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[420px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : rows.length > 0 ? (
          <table className="w-full text-sm text-left min-w-[820px]">
            <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 font-medium">Item Code</th>
                <th className="px-4 py-3 font-medium">Item Name</th>
                <th className="px-4 py-3 font-medium text-right">Qty Sold</th>
                <th className="px-4 py-3 font-medium text-right">Sales Amount</th>
                {/* <th className="px-4 py-3 font-medium text-right">Discount</th>
                <th className="px-4 py-3 font-medium text-right">Net Amount</th> */}
                <th className="px-4 py-3 font-medium text-right">Avg Selling Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map((row: any) => (
                <tr key={`${row.item}-${row.variant || ''}`} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{row.item_code}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {row.item_name}
                    {row.variant_name ? ` — ${row.variant_name}` : ''}
                  </td>
                  <td className="px-4 py-3 text-right">{row.qty_sold}</td>
                  <td className="px-4 py-3 text-right">{formatMoney(row.sales_amount)}</td>
                  {/* <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.discount)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatMoney(row.net_amount)}</td> */}
                  <td className="px-4 py-3 text-right">{formatMoney(row.avg_selling_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No item sales for the selected filters</p>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={rows.length} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
